import { UAParser } from 'ua-parser-js';

import { type PicoXConfiguration } from '../models/configuration';
import { sessionStorageIdGenerator } from './utils/generators';

/**
 * Context provider for web applications.
 *
 * This provider implements a method 'currentContext' which
 * returns client-speicific information such as device, browser,
 * language, locale, timezone, etc.
 */
export type ContextProvider = {
  /** Returns the current context */
  currentContext: (currentDate: Date) => Context;
};

export const createContextProvider = (configuration: PicoXConfiguration): ContextProvider => {
  return {
    currentContext: (currentDate: Date) => {
      if (typeof window === 'undefined') {
        throw new Error('ContextProvider is not available in the server');
      }

      const parser = new UAParser(window.navigator.userAgent);
      const deviceInfo = parser.getResult();
      const locationInfo = Intl.DateTimeFormat().resolvedOptions();
      const sdkVersion = configuration.sdkVersion;

      /**
       * NOTE: parsing UserAgent is not a correct science.
       * This is why we must assume some fields may be null.
       * Although, in the majority of the interesting cases, they will not be null.
       */
      const deviceBrowser = deviceInfo.browser.name ?? null;
      const deviceOs = deviceInfo.os.name ?? null;
      const deviceOsVersion = deviceInfo.os.version ?? null;
      /**
       * NOTE: 'desktop' is not a possible device type.
       * UAParser only reports info directly available from the UA string, which is not the case for 'desktop' device type.
       * If you wish to detect desktop devices, you must handle the needed logic yourself.
       * You can read more about it in this issue: https://github.com/faisalman/ua-parser-js/issues/182
       */
      const deviceType = deviceInfo.device.type ?? 'desktop';

      const language = window.navigator.language;
      const locale = locationInfo.locale;
      const timezoneDaylightSaving = isDaylightSaving(currentDate);
      const timezoneName = locationInfo.timeZone;
      const timezoneSeconds = currentDate.getTimezoneOffset() * 60;

      // Generate a session ID that persists for the current browser session
      // A new ID is created when opening a new tab or restoring after browser restart
      //
      // For full information of its lifecycle see:
      // https://developer.mozilla.org/en-US/docs/Web/API/Window/sessionStorage
      const sessionId = sessionStorageIdGenerator('PICOX_SESSION_ID');

      return {
        device_browser: deviceBrowser,
        device_os: deviceOs,
        device_os_version: deviceOsVersion,
        device_type: deviceType,
        language,
        locale,
        session_id: sessionId(),
        sdk_version: sdkVersion,
        timezone_daylight_saving: timezoneDaylightSaving,
        timezone_name: timezoneName,
        timezone_seconds: timezoneSeconds,
      };
    },
  };
};

/**
 * Checks if the current timezone is in daylight saving time.
 *
 * @param date - The date to check.
 * @returns True if the timezone is in daylight saving time, false otherwise.
 */
const isDaylightSaving = (date = new Date()) => {
  const january = new Date(date.getFullYear(), 0, 1).getTimezoneOffset();
  const july = new Date(date.getFullYear(), 6, 1).getTimezoneOffset();
  return Math.max(january, july) !== date.getTimezoneOffset();
};

/**
 * Context information about the environment where events are generated
 */
export type Context = {
  /**
   * The browser being used (e.g. "Chrome", "Firefox", "Safari")
   */
  device_browser: string | null;

  /**
   * The operating system (e.g. "Windows", "macOS", "iOS")
   */
  device_os: string | null;

  /**
   * The version of the operating system (e.g. "10", "13.1")
   */
  device_os_version: string | null;

  /**
   * The type of device (console, mobile, tablet, smarttv, wearable, embedded)
   */
  device_type: string;

  /**
   * The application's configured language code (e.g. "es", "en")
   */
  language: string;

  /**
   * The locale identifier (e.g. "es-ES", "en-US")
   */
  locale: string;

  /**
   * Unique identifier for the current session
   */
  session_id: string;

  /**
   * The version of the Pico SDK
   */
  sdk_version: string;

  /**
   * Whether daylight savings time is active
   */
  timezone_daylight_saving: boolean;

  /**
   * The IANA timezone identifier (e.g. "America/Denver")
   */
  timezone_name: string;

  /**
   * Offset from UTC in seconds
   */
  timezone_seconds: number;
};
